import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { FoundingBadge } from "@/components/FoundingBadge";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export default function PublicAvailability() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/share/${shareId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [shareId]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <div className="mono text-xs tracking-[0.2em] uppercase text-brand">jeeo</div>
          <h1 className="font-display text-2xl font-semibold mt-2">Link not found</h1>
          <p className="text-muted-foreground text-sm mt-1">This availability link is invalid or was removed.</p>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="mono text-xs tracking-[0.2em] uppercase text-brand animate-pulse">Loading…</div>
      </div>
    );

  return (
    <div className="min-h-screen noise">
      <header className="backdrop-blur-xl bg-white/80 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center text-white">
            <MapPin size={16} strokeWidth={1.5} />
          </div>
          <span className="font-display text-lg font-semibold">jeeo</span>
          <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground border-l border-border pl-2 ml-1">
            TAMU
          </span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-6">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Weekly free time</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-1">
          {data.name}'s availability
        </h1>
        {data.founding_member && <FoundingBadge className="mb-2" />}
        <p className="text-sm text-muted-foreground mb-6">
          Open windows between classes on the main campus. Grab them to meet up.
        </p>

        <div className="space-y-4">
          {DAYS.map((d, i) => {
            const day = data.days[d];
            return (
              <motion.div
                key={d}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-border rounded-xl p-5"
                data-testid={`avail-${d}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl font-semibold">{d}</h3>
                  <span className="mono text-xs text-muted-foreground">
                    {day.classes.length} classes
                  </span>
                </div>
                {day.windows.length === 0 ? (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <CalendarClock size={15} strokeWidth={1.5} />
                    {day.classes.length === 0 ? "No classes — likely free all day." : "No gaps between classes."}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {day.windows.map((w, j) => (
                      <div
                        key={j}
                        className="flex items-center gap-2 bg-[#16A34A]/10 text-[#166534] rounded-md px-3 py-2"
                      >
                        <span className="mono text-sm font-bold">{w.duration_min}m</span>
                        <span className="mono text-xs">{w.start} – {w.end}</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <a
          href="/"
          className="mt-8 inline-flex items-center gap-2 bg-brand text-white rounded-md px-4 py-3 text-sm font-semibold hover:opacity-90 transition-colors"
        >
          Plan your own routes on jeeo
        </a>
      </div>
    </div>
  );
}
